import { type ColumnDef } from "@tanstack/react-table";
import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import {
  DateTableCell,
  PaginationControl,
  ReactTableComponent,
} from "../../../../components/ui-kit";
import { AppRoutes } from "../../../../constants/routes";
import { SupplierApiType } from "../../../../util/types";
import { FlagIcon, FlagIconCode } from "react-flag-kit";
import styled from "styled-components";
import { CURRENCIES, CurrencyCodes } from "@shared_packages/toolkit/types";

const FlagContainer = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
`;

const StyledFlagIcon = styled(FlagIcon)`
  height: 50%;
`;

const CountryFlagIcon: React.FunctionComponent<{
  code: FlagIconCode;
}> = ({ code }) => (
  <FlagContainer>
    <StyledFlagIcon code={code} />
  </FlagContainer>
);

const CurrencyWrapper = styled.span<{ disabled?: boolean }>`
  font-family: Montserrat;
  font-size: 13px;
  line-height: 20px;
  font-style: normal;
  font-weight: 400;
  display: flex;
  align-items: center;
  color: #13273f;
  min-width: max-content;
`;

interface SuppliersTableProps {
  data: SupplierApiType[];
  pagination: PaginationControl;
}

const SuppliersTable: FC<SuppliersTableProps> = ({ data, pagination }) => {
  const navigate = useNavigate();
  const onRowClick = (row: SupplierApiType) => {
    navigate(`${AppRoutes.Private.Inventory.SUPPLIERS_DETAILS + row._id}`);
  };

  const columns = React.useMemo<Array<ColumnDef<SupplierApiType, any>>>(
    () => [
      {
        header: "Name",
        meta: { mobileTitle: true },
        accessorKey: "name",
        enableSorting: false,
        cell: (info) => info.getValue(),
      },
      {
        header: "Currency",
        accessorKey: "currency",
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const supplierCurrency =
            CURRENCIES[original.currency as CurrencyCodes];

          return original.currency ? (
            <CurrencyWrapper>
              <CountryFlagIcon
                code={(supplierCurrency?.countryCode ?? "GB") as FlagIconCode}
              />
              ({supplierCurrency?.symbol}) {original?.currency}
            </CurrencyWrapper>
          ) : (
            "-"
          );
        },
      },
      {
        header: "Owner",
        accessorKey: "owner.tradingName",
        enableSorting: false,
        cell: (info) => info.getValue(),
      },
      {
        header: "Created at",
        accessorKey: "createdAt",
        enableSorting: false,
        cell: (info) => <DateTableCell date={info.getValue()} />,
      },
    ],
    []
  );

  return (
    <>
      <ReactTableComponent
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        withPagination={pagination}
        withSorting
      />
    </>
  );
};

export default SuppliersTable;
